import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import api from '@/lib/api';
import type { BackupOverallStatus, DbHealthSummaryResponse } from '@easyflow/shared';

const SHELL: Record<BackupOverallStatus, string> = {
  ok: 'border-emerald-300 bg-emerald-500/5',
  warning: 'border-amber-300 bg-amber-500/10',
  critical: 'border-rose-400 bg-rose-500/10',
  unknown: 'border-slate-300 bg-slate-500/5',
};

const TEXT: Record<BackupOverallStatus, string> = {
  ok: 'text-emerald-700 dark:text-emerald-300',
  warning: 'text-amber-700 dark:text-amber-300',
  critical: 'text-rose-700 dark:text-rose-300',
  unknown: 'text-slate-700 dark:text-slate-300',
};

interface AnomaliesBannerProps {
  onOpenRestore?: () => void;
}

export function AnomaliesBanner({ onOpenRestore }: AnomaliesBannerProps) {
  const [data, setData] = useState<DbHealthSummaryResponse | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get<DbHealthSummaryResponse>('/master/database/health-summary');
      if (res.data) setData(res.data);
    } catch {
      /* fail-soft: il banner non deve rompere la pagina */
    }
  }, []);

  useEffect(() => {
    void fetchSummary();
    const interval = setInterval(() => { void fetchSummary(); }, 25000);
    return () => clearInterval(interval);
  }, [fetchSummary]);

  if (!data) return null;

  const Icon = data.overall === 'ok' ? CheckCircle2 : data.overall === 'critical' ? ShieldAlert : AlertTriangle;

  return (
    <div className={`sticky top-0 z-20 rounded-lg border px-4 py-3 ${SHELL[data.overall]}`}>
      <div className="flex items-center justify-between gap-3">
        <div className={`flex items-center gap-2 text-sm font-medium ${TEXT[data.overall]}`}>
          <Icon className="h-4 w-4 shrink-0" />
          {data.overall === 'ok'
            ? 'Nessuna anomalia rilevata'
            : `${data.critical_count} critiche · ${data.warning_count} warning`}
          {data.critical_count > 0 && (
            <Badge className="bg-rose-500/20 text-rose-700 border-rose-500/40">{data.critical_count} critical</Badge>
          )}
          {data.warning_count > 0 && (
            <Badge className="bg-amber-500/20 text-amber-700 border-amber-500/40">{data.warning_count} warning</Badge>
          )}
        </div>
        {data.anomalies.length > 0 && (
          <Button variant="ghost" size="sm" className="h-7 gap-1" onClick={() => setExpanded((e) => !e)}>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {expanded ? 'Nascondi' : 'Dettagli'}
          </Button>
        )}
      </div>
      {expanded && data.anomalies.length > 0 && (
        <div className="mt-2 space-y-1 border-t pt-2">
          {data.anomalies.map((a) => (
            <div key={a.code} className="flex items-center gap-2 text-xs">
              <Badge
                className={a.severity === 'critical'
                  ? 'bg-rose-500/20 text-rose-700 border-rose-500/40'
                  : 'bg-amber-500/20 text-amber-700 border-amber-500/40'}
              >
                {a.severity}
              </Badge>
              <code className="text-[11px]">{a.code}</code>
              <span className="min-w-0 flex-1 text-muted-foreground">{a.message}</span>
              {a.code.startsWith('restore_') && onOpenRestore && (
                <Button variant="outline" size="sm" className="h-7 shrink-0" onClick={onOpenRestore}>
                  Apri verifiche
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
